import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

const PAGE_SIZE = 8;

/**
 * Creates the My Courses view-state store for the current browser session.
 *
 * This store only owns UI state for the My Courses page, for example the
 * selected tab, current/past view, page number, and page size. Course data is
 * still loaded by the page queries.
 *
 * Components subscribe to individual fields/actions with selectors such as
 * `useMyCoursesViewStore((state) => state.isPast)`. The `state` argument is
 * supplied by Zustand and contains every key defined in the store object below.
 *
 * The `partialize` option only controls which serializable values are persisted
 * to sessionStorage. Actions are intentionally excluded because functions are
 * recreated by Zustand and should not be saved to browser storage.
 */
export const useMyCoursesViewStore = create(
    persist(
        (set) => ({
            isPast: 0,
            showQual: true,
            limit: PAGE_SIZE,
            pastLimit: PAGE_SIZE,
            showPast: false,
            page: 1,
            pagePast: 1,
            setShowQual: (showQual) => set({ showQual }),
            setShowPast: (showPast) => set({ showPast }),
            selectQualifications: () =>
                set({
                    showQual: true,
                    isPast: 0,
                    limit: PAGE_SIZE,
                    page: 1,
                    pagePast: 1,
                }),
            selectShortCourses: () =>
                set({
                    showQual: false,
                    isPast: 0,
                    limit: PAGE_SIZE,
                    page: 1,
                    pagePast: 1,
                }),
            showCompletedCourses: () =>
                set((state) => {
                    const shouldShowPast = state.isPast !== 1;

                    return {
                        isPast: shouldShowPast ? 1 : 0,
                        showPast: shouldShowPast,
                    };
                }),
        }),
        {
            name: 'mycourses-view-state',
            storage: createJSONStorage(() => sessionStorage),
            partialize: ({ isPast, showQual, limit, pastLimit, showPast, page, pagePast }) => ({
                isPast,
                showQual,
                limit,
                pastLimit,
                showPast,
                page,
                pagePast,
            }),
        },
    ),
);
